import { Box, Button, Tooltip } from "@mui/material";

import { ButtonGroup } from "@mui/material";
import { saveAs } from "file-saver";
import { asBlob } from "html-docx-js-typescript";
import { useRef } from "react";
import Markdown from "react-markdown";
import { useReactToPrint } from "react-to-print";
import remarkGfm from "remark-gfm";
import CRSearchList from "../../cr-search/CRSearchList";
import ArtifactDisplay from "../ArtifactDisplay";
import styles from "./ToolTrackerResult.module.css";

const ToolTrackerResult = ({
  result,
  isError,
  error,
  isLoading,
}: {
  result: string | undefined | null;
  isError: boolean;
  error: Error | null;
  isLoading: boolean;
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const markdownRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "",
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
        @top-left { content: ""; }
        @top-center { content: ""; }
        @top-right { content: ""; }
        @bottom-left { content: ""; }
        @bottom-center { content: ""; }
        @bottom-right { content: ""; }
      }
    `,
  });

  const handleSaveAsDoc = async () => {
    if (!markdownRef.current) return;

    try {
      // Get the HTML content from the rendered Markdown
      const htmlContent = markdownRef.current.innerHTML;

      // Wrap in a complete HTML document structure
      const fullHtmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; }
              h1, h2, h3 { color: #333; }
              pre { background: #f4f4f4; padding: 10px; border-radius: 4px; }
              code { background: #f4f4f4; padding: 2px 4px; border-radius: 2px; }
              table { border-collapse: collapse; width: 100%; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
        </html>
      `;

      // Convert HTML to DOCX blob
      const docxResult = await asBlob(fullHtmlContent);

      // Ensure we have a proper Blob for file-saver
      let docxBlob: Blob;
      if (docxResult instanceof Blob) {
        docxBlob = docxResult;
      } else {
        // Convert Buffer to ArrayBuffer for Blob constructor
        const uint8Array = new Uint8Array(docxResult);
        docxBlob = new Blob([uint8Array], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      }

      // Generate filename with timestamp
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `assistant-result-${timestamp}.docx`;

      // Save the file
      saveAs(docxBlob, filename);
    } catch (error) {
      console.error("Error saving as DOC:", error);
      // You might want to show a toast notification here
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "a") {
      event.preventDefault();
      event.stopPropagation();

      if (markdownRef.current) {
        const range = document.createRange();
        range.selectNodeContents(markdownRef.current);

        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
  };

  const removeThinkTags = (text: string): string => {
    return text.replace(/<think>[\s\S]*?<\/think>/gi, "").trim();
  };

  // Helper function to check if result is an artifact result object
  const isArtifactResult = (result: string): boolean => {
    try {
      const parsed = JSON.parse(result);
      return (
        typeof parsed === "object" &&
        parsed !== null &&
        "artifact_id" in parsed &&
        "message" in parsed
      );
    } catch {
      return false;
    }
  };

  // Helper function to parse artifact result
  const parseArtifactResult = (result: string) => {
    try {
      return JSON.parse(result);
    } catch {
      return null;
    }
  };

  if (isError) {
    return <div>Error: {error?.message}</div>;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (result) {
    // Check if the result is an object that contains assistant_response_crs with nested structure
    if (
      typeof result === "object" &&
      result !== null &&
      (result as any).assistant_response_crs &&
      (result as any).assistant_response_crs.assistant_response_crs &&
      Array.isArray(
        (result as any).assistant_response_crs.assistant_response_crs,
      )
    ) {
      const crSearchData = (result as any).assistant_response_crs
        .assistant_response_crs;
      console.log(result);
      return (
        <Box sx={{ width: "100%" }}>
          <CRSearchList crSearchResults={crSearchData} result={result} />
        </Box>
      );
    }

    // Otherwise render as markdown
    const cleanedResult = removeThinkTags(String(result));

    // Check if this is an artifact result
    if (isArtifactResult(cleanedResult)) {
      const artifactData = parseArtifactResult(cleanedResult);

      return (
        <Box sx={{ width: "100%" }}>
          <ArtifactDisplay
            artifactId={artifactData.artifact_id}
            artifactName={artifactData.artifact_name}
            message={artifactData.message}
          />
        </Box>
      );
    }

    // Regular markdown display for non-artifact results
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ButtonGroup variant="contained" disableElevation>
            <Tooltip title="Print / Save as PDF">
              <Button color="primary" onClick={handlePrint}>
                Save as PDF
              </Button>
            </Tooltip>
            <Tooltip title="Save as Microsoft Word Document">
              <Button color="primary" onClick={handleSaveAsDoc}>
                Save as DOC
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
        <div ref={componentRef}>
          <div
            ref={markdownRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            style={{ outline: "none" }}
          >
            <Markdown
              remarkPlugins={[remarkGfm]}
              className={styles.markdownContainer}
              components={{
                a: ({ node, ...props }) => <a {...props} target="_blank" />,
              }}
            >
              {cleanedResult}
            </Markdown>
          </div>
        </div>
      </Box>
    );
  }

  return null;
};

export default ToolTrackerResult;
